import React from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../UserContext';
import { PLAN_CONFIG } from '../planConfig';
import '../components/ActivePlanPage.css';

const QUOTA_LABELS = {
  searches: 'Tender Discovery Credits',
  results: 'Results Analysis Credits',
  tmBids: 'Bid Analyses',
  reportRows: 'Report Rows',
};

const BAR_COLORS = {
  searches: '#1976d2',
  results: '#1565c0',
  tmBids: '#7b1fa2',
  reportRows: '#0097a7',
};

function UsageMeter({ label, used, limit, bonus, color }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const remainPct = Math.max(0, 100 - pct);
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ color: pct >= 90 ? '#d32f2f' : '#555', fontWeight: pct >= 90 ? 600 : 400 }}>
          {pct}% used
          {bonus > 0 && <span style={{ color: '#43a047', marginLeft: 6 }}>(bonus active)</span>}
        </span>
      </div>
      <div style={{ background: '#e0e0e0', borderRadius: 6, height: 14, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: pct >= 90 ? '#d32f2f' : color,
          borderRadius: 6, transition: 'width 0.3s',
        }} />
      </div>
      <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
        {remainPct}% remaining
      </div>
    </div>
  );
}

const ActivePlanPage = () => {
  const navigate = useNavigate();
  const {
    isSubscribed,
    planType,
    endDate,
    startDate,
    status,
    quota,
  } = useUserContext();

  const toDate = (value) => {
    if (!value) return null;
    if (value.toDate && typeof value.toDate === 'function') {
      try { return value.toDate(); } catch { /* ignore */ }
    }
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'object' && value !== null && typeof value.seconds === 'number')
      return new Date(value.seconds * 1000);
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (value) => {
    const d = toDate(value);
    if (!d) return 'N/A';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const daysLeft = (() => {
    const dEnd = toDate(endDate);
    if (!dEnd) return 0;
    const ms = dEnd.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  const planCfg = PLAN_CONFIG[(planType || '').toLowerCase()];
  const planLabel = planCfg ? planCfg.label + ' Plan' : (planType || 'N/A');

  if (!isSubscribed) {
    return (
      <Layout>
        <div className="active-plan-container">
          <h1>Active Plan Details</h1>
          <div className="no-plan-details">
            <p>No active plan. Please subscribe to a plan.</p>
            <button className="subscribe-button" onClick={() => navigate('/subscription')}>
              Subscribe Now
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="active-plan-container">
        <h1>Active Plan Details</h1>

        <div className="plan-details">
          {/* Plan info */}
          <h2>{planLabel}</h2>
          <p><strong>Status:</strong> {status || 'N/A'}</p>
          <p><strong>Valid From:</strong> {formatDate(startDate)}</p>
          <p><strong>Valid Until:</strong> {formatDate(endDate)}</p>
          <p><strong>Days Left:</strong> {daysLeft}</p>

          {/* Quota usage meters */}
          {quota && (
            <div style={{ marginTop: 24 }}>
              <h2>Usage This Cycle</h2>
              <p style={{ fontSize: 13, color: '#777', marginBottom: 16 }}>
                Cycle: {formatDate(quota.cycleStart)} &mdash; {formatDate(quota.cycleEnd)}
              </p>

              {['searches', 'results', 'tmBids', 'reportRows'].map((key) => {
                const q = quota[key];
                if (!q) return null;
                return (
                  <UsageMeter
                    key={key}
                    label={QUOTA_LABELS[key]}
                    used={q.used || 0}
                    limit={q.limit || 0}
                    bonus={q.bonus || 0}
                    color={BAR_COLORS[key]}
                  />
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="manage-subscription-button"
              onClick={() => navigate('/subscription-pricing')}
            >
              Change Plan
            </button>
            <button
              className="manage-subscription-button secondary"
              onClick={() => navigate('/subscription')}
            >
              Purchase Add-ons
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ActivePlanPage;
